import { useState, type ReactNode } from "react";
import { Link, Outlet } from "react-router-dom";
import { useIsAdmin } from "../auth/useIsAdmin.js";

/**
 * Layout shell for /admin/* routes. Hidden from normal nav. The server
 * enforces auth on every admin call; this guard just avoids rendering the
 * UI shell to non-admins so they don't see broken affordances.
 */
export function AdminShell() {
  const { loading, isAdmin } = useIsAdmin();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (!isAdmin) {
    return <ForbiddenPage />;
  }
  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/admin" className="text-base font-semibold text-slate-900">
            Admin · RFQs
          </Link>
          <Link
            to="/app"
            className="rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            Exit
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-4">
        <Outlet />
      </main>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-4 text-center">
      <h1 className="text-xl font-semibold text-slate-900">Admin access required</h1>
      <p className="mt-2 text-sm text-slate-600">
        Your account is not a member of the <code>admins</code> group.
      </p>
      <Link
        to="/app"
        className="mt-4 rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
      >
        Back to app
      </Link>
    </div>
  );
}

export function AdminCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">{children}</div>
  );
}

const STATUS_TONE: Record<string, string> = {
  SUBMITTED: "bg-teal-50 text-teal-800 border-teal-200",
  IN_REVIEW: "bg-amber-50 text-amber-800 border-amber-200",
  CLOSED: "bg-slate-100 text-slate-700 border-slate-200",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

export function useToggle(initial = false): [boolean, () => void] {
  const [v, setV] = useState(initial);
  return [v, () => setV((x) => !x)];
}
